import React, { Suspense, lazy } from "react";
import { Dropdown, Segment, Button, Label, Dimmer, Loader } from "semantic-ui-react";
import { endOfDay, format, startOfDay } from "date-fns";

import { DateRangeSelector } from "../components/date-range-selector.component";
import { useSelector, useDispatch } from "react-redux";
import { setRangeType, setFirstDateRange, setSecondDateRange, loadStats } from "./actions";
import { DateTimeFormat } from "utilities/dist/invoice/constants";
import { parseCurrency } from "../../common/helpers";
import { isFeatureActive, OrderFeature } from "../../company/feature/helpers";

const BarChartForTotal = lazy(() => import("../components/bar-chart"));

const ReportsContainer = () => {
    const dispatch = useDispatch();
    const { company } = useSelector(({ auth }) => ({
        company: auth.user.company,
    }));
    const hasOrderFeature = isFeatureActive(company, OrderFeature);

    const { rangeType, firstRange, secondRange, firstData, secondData, reportDate, loading } =
        useSelector(({ reports }) => ({
            rangeType: reports.rangeType,
            firstRange: reports.firstRange,
            secondRange: reports.secondRange,
            firstData: reports.firstData,
            secondData: reports.secondData,
            reportDate: reports.reportDate,
            loading: reports.loading,
        }));

    const rangeTypes = ["Monthly", "Weekly"];

    const onChangeRangeType = (e, { value }) => {
        dispatch(setRangeType(value));
    };

    const handleDateChange = (dateRange, order) => {
        if (!dateRange) return;

        const [from, to] = dateRange;

        const setter = order === 1 ? setFirstDateRange : setSecondDateRange;

        dispatch(setter(`${format(from, "dd MMM, yyyy")} - ${format(to, "dd MMM, yyyy")}`));
        dispatch(loadStats({ from, to }, order));
    };

    const refreshData = () => {
        const [firstFromDateString, firstToDateString] = firstRange.split(" - ");
        const [secondFromDateString, secondToDateString] = secondRange.split(" - ");

        dispatch(
            loadStats(
                {
                    from: startOfDay(new Date(firstFromDateString)),
                    to: endOfDay(new Date(firstToDateString)),
                },
                1
            )
        );
        dispatch(
            loadStats(
                {
                    from: startOfDay(new Date(secondFromDateString)),
                    to: endOfDay(new Date(secondToDateString)),
                },
                2
            )
        );
    };

    return (
        <Segment className="reports-container">
            <div className="pt-8">
                <span> Comparison Range </span>
                <Dropdown
                    fluid
                    selection
                    options={rangeTypes.map(range => ({
                        key: range,
                        text: range,
                        value: range,
                    }))}
                    value={rangeType}
                    onChange={onChangeRangeType}
                />
            </div>
            <div className="tm-10 flex">
                <div>
                    <span> Compare </span>
                    <DateRangeSelector
                        type={rangeType}
                        onChangeDateRange={value => handleDateChange(value, 1)}
                        value={firstRange}
                    />
                </div>
                <div className="lm-5">
                    <span> with </span>
                    <DateRangeSelector
                        type={rangeType}
                        onChangeDateRange={value => handleDateChange(value, 2)}
                        value={secondRange}
                    />
                </div>
            </div>
            {firstData && secondData && (
                <Segment>
                    {loading && (
                        <Dimmer active inverted>
                            <Loader />
                        </Dimmer>
                    )}
                    {reportDate && (
                        <div className="tm-20 flex">
                            <div className="flex flex-col justify-center">
                                <Label>{`Generated at ${format(
                                    new Date(reportDate),
                                    DateTimeFormat
                                )}`}</Label>
                            </div>
                            <div className="lm-10">
                                <Button
                                    size="mini"
                                    circular
                                    content="Refresh"
                                    icon="refresh"
                                    onClick={refreshData}
                                />
                            </div>
                        </div>
                    )}
                    <div className="tm-50 flex">
                        <Suspense fallback={<Loader content="Loading..." />}>
                            <BarChartForTotal
                                data={[
                                    {
                                        name: "Invoice total",
                                        [`${firstRange}`]: parseCurrency(firstData.invoiceTotal),
                                        [`${secondRange}`]: parseCurrency(secondData.invoiceTotal),
                                    },
                                ]}
                                dataKeys={[
                                    { key: `${firstRange}`, fill: "#8884d8" },
                                    { key: `${secondRange}`, fill: "#82ca9d" },
                                ]}
                            />
                            {hasOrderFeature && (
                                <BarChartForTotal
                                    data={[
                                        {
                                            name: "Order total",
                                            [`${firstRange}`]: parseCurrency(firstData.orderTotal),
                                            [`${secondRange}`]: parseCurrency(
                                                secondData.orderTotal
                                            ),
                                        },
                                    ]}
                                    dataKeys={[
                                        { key: `${firstRange}`, fill: "#8884d8" },
                                        { key: `${secondRange}`, fill: "#82ca9d" },
                                    ]}
                                />
                            )}
                        </Suspense>
                    </div>
                </Segment>
            )}
        </Segment>
    );
};

export default ReportsContainer;
